import { Component, OnInit } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Lambda } from 'aws-sdk';

const SIZE = 10;
const RETRY = 3;

@Component({
  selector: 'app-ctr-operations',
  templateUrl: './ctr-operations.component.html',
  styleUrls: ['./ctr-operations.component.css']
})
export class CtrOperationsComponent implements OnInit {

  // page variables
  lambdaMessage: string = "";
  lambdaError: boolean = false;

  showProgressBar: boolean = false;
  disableButtons: boolean = true;
  reprocessSuccessCount: Number = 0;
  numberOfRecords: Number = 0;

  contactIdSearch: string = "";
  filterString: string = "";
  errorOnly: boolean = false;

  // metadata
  metadata: any = {};                                                 //contains page relevant info for each ctr
  defaultMetadata: any = {readSuccess: false, updateSuccess: false, valid: false, dirty: false}

  // table variables
  footerMessage: string = '';
  formValid: boolean = false;
  showTable: boolean = false;
  itemsPerPage: Number = 10;
  objectBluePrint: any = { contactId: "", Attributes: { claimID: "", startTime: "", endTime: "", agentUsername: "", info: ""} };
  tableData: Array<any> = [];
  filteredData: Array<any> = [];

  constructor() { }

  ngOnInit() {
  }

  //retreive single ctr from connect
  searchContactRecord(inputContactId: string, rowUpdate: boolean = false) {
    this.lambdaMessage = "";
    this.lambdaError = false;
    let contactList = [{ contactId: inputContactId.trim() }];

    //handle lambda call
    this.contactRecordsLambda('read', contactList).then(payload => {
      console.info("ctr lambda read returned: ", payload);
      if (payload.statusCode == 200) {
        this.updateMetadata(contactList, {readSuccess: true, info: ""});
        if (rowUpdate) {
          this.validateAll();
          this.lambdaMessage = "record has been refreshed"
        } else {
          this.tableData = payload.body.Item;
          this.validateAll();
          this.applyFilter();
          this.showTable = true;
          this.disableButtons = false;
        }
      } else {
        console.error(payload);
        this.updateMetadata(contactList, {readSuccess: false, info: "read failure"});
        this.lambdaMessage = "Failed to retrieve ctr data from connect";
        this.lambdaError = true;
      }
    }).catch(err => {
      console.error("ctr lambda error: ", err);
      this.updateMetadata(contactList, {readSuccess: false, info: "read failure"});
      this.lambdaMessage = "Failed to retrieve ctr data from connect";
      this.lambdaError = true;
    });
  }

  // reprocess contact records in batch
  async reprocessContactRecords() {
    //initialize values
    let reprocessList = JSON.parse(JSON.stringify(this.tableData));
    this.lambdaMessage = "";
    this.lambdaError = false;
    this.reprocessSuccessCount = 0;
    this.numberOfRecords = reprocessList.length;
    this.showProgressBar = true;
    this.disableButtons = true;
    let consecutiveFailures = 0;
    let batches: Array<any> = [];
    while (reprocessList.length > 0) {
      batches.push(reprocessList.splice(0, SIZE));
    }

    //process in loop
    console.log("starting batch reprocessing");
    while (batches.length > 0) {
      let currentBatch = batches[0]
      let result = await this.contactRecordsLambda('update', currentBatch);
      console.log("ctr lambda reprocess returned ", result);
      if (result.statusCode == 200) {
        this.updateMetadata(batches.splice(0,1)[0], {reprocessSuccess: true, info: ""});
        this.reprocessSuccessCount += currentBatch.length;
        consecutiveFailures = 0;
      } else {
        consecutiveFailures++;
        console.info("consecutiveFailures: ", consecutiveFailures);
        if (consecutiveFailures > RETRY) {
          this.updateMetadata(batches.splice(0,1)[0], {reprocessSuccess: false, info: "reprocess failure"});
          console.error("Retries Exceeded. Error: ", result);
          consecutiveFailures = 0;
        } else {
          console.warn("A batch failed to reprocess. error: ", result);
          await this.delay(1000 * Math.min(8, Math.pow(consecutiveFailures, 2)));
        }
      }
    }

    //update values
    console.log("end batch reprocessing");
    this.showProgressBar = false;
    this.disableButtons = false;
    this.lambdaError = false;
    this.lambdaMessage = `Successfully initiated reprocess for ${this.reprocessSuccessCount} out of ${this.numberOfRecords} records`;
  }

  // retrieve contact records in batch
  async readContactRecords(readList: Array<any>) {
    //initialize values
    this.lambdaMessage = "Retrieving contact trace records. This may take a few minutes.";
    this.lambdaError = false;
    this.reprocessSuccessCount = 0;
    this.numberOfRecords = readList.length;
    this.showProgressBar = true;
    this.disableButtons = true;
    let consecutiveFailures = 0;
    let batches: Array<any> = [];
    while (readList.length > 0) {
      batches.push(readList.splice(0, SIZE));
    }

    //read in loop
    console.info("starting batch read");
    while (batches.length > 0) {
      console.log("batches remaining: ", batches.length);
      let currentBatch = batches[0];
      let result = await this.contactRecordsLambda('read', currentBatch);
      let success = (result.statusCode == 200);
      if (success) {
        this.updateMetadata(batches.splice(0,1)[0], {readSuccess: true, info: ""});
        this.tableData = this.tableData.concat(result.body.Item);
        this.applyFilter();
        this.reprocessSuccessCount += currentBatch.length;
        consecutiveFailures = 0;
      } else {
        consecutiveFailures++;
        console.info("consecutiveFailures: ", consecutiveFailures);
        if (consecutiveFailures > RETRY) {
          console.error("Retries Exceeded. Error: ", result);
          let failedData = batches.splice(0, 1)[0];
          this.updateMetadata(failedData, {readSuccess: false, info: "read failure"});
          this.tableData = this.tableData.concat(failedData);
          this.applyFilter();
        } else {
          console.warn("A batch failed to read. error: ", result);
          await this.delay(1000 * Math.min(8, Math.pow(consecutiveFailures, 2)));
        }
      }
    }
    //update values
    console.info("end batch read");
    this.validateAll();
    this.showProgressBar = false;
    this.disableButtons = false;
    this.lambdaError = false;
    this.lambdaMessage = `Successfully read ${this.reprocessSuccessCount} out of ${this.numberOfRecords} records`;
  }

  contactRecordsLambda(op: string, contactList: Array<any>): Promise<any> {
    let params = {
      FunctionName: environment.ContactTraceRecordLambdaName,
      Payload: JSON.stringify({
        operation: op,
        data: contactList
      })
    }
    const promise = new Promise((resolve, reject) => {
      new Lambda().invoke(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          try {
            let response = (data as any);
            let payload = JSON.parse(response.Payload);
            resolve(payload);
          } catch (err) {
            reject(err);
          }
        }
      });
    });
    return promise;
  }
  
  updateMetadata(items: Array<any>, data: any={}){
    items.map(item => {
      if(!this.metadata[item.contactId]){
        this.metadata[item.contactId] = JSON.parse(JSON.stringify(this.defaultMetadata));
      }
      Object.assign(this.metadata[item.contactId], data);
    })
  }

  applyFilter() {
    this.filteredData = [];
    this.tableData.map(x => {
      if (x.Attributes.hasOwnProperty('claimID') && x.Attributes.claimID.includes(this.filterString)) {
        this.filteredData.push(x);
      } else if (x.Attributes.hasOwnProperty('agentUsername') && x.Attributes.agentUsername.includes(this.filterString)) {
        this.filteredData.push(x);
      } else if (x.contactId.includes(this.filterString)) {
        this.filteredData.push(x);
      }
    });
    if(this.errorOnly){
      let temp = [];
      for(let x=0; x<this.filteredData.length; x++){
        let meta = this.metadata[this.filteredData[x].contactId];
        if(!(meta.valid && meta.readSuccess)){
          temp.push(this.filteredData[x]);
        }
      }
      this.filteredData = temp;
    }
  }

  replaceRowInTable(item: any) {
    for(let x=0; x<this.tableData.length; x++){
      if (this.tableData[x].contactId == item.contactId) {
        this.tableData[x] = item;
        break;
      }
    }
    this.applyFilter();
  }

  deleteRowInTable(item:any){
    if(confirm("Are you sure you want to remove this item?")){
      console.log("removing item: ", item);
      for(let x=0; x<this.tableData.length; x++){
        if (this.tableData[x].contactId == item.contactId) {
          this.tableData.splice(x, 1);
          break;
        }
      }
      this.applyFilter();
    }
  }

  //attributes may not be present in item
  getAttribute(item: any, attribute: string) {
    if (item.Attributes.hasOwnProperty(attribute)) {
      return item.Attributes[attribute];
    }
    return "";
  }

  //if user edits a column, add attribute if not present
  includeProperty(attributes, key) {
    if (!attributes.hasOwnProperty(key)) {
      console.log("Adding missing key: ", key)
      attributes[key] = "";
    }
  }

  //if user removes an attribute, remove it from item
  removeEmptyProperties(item) {
    let keys = Object.keys(item.Attributes);
    keys.map(key => {
      if (item.Attributes[key] === "") {
        console.log("removing key: ", key)
        delete item.Attributes[key];
      }
    })
  }

  //checks every item in data to see if valid and no reprocess error
  reprocessAllowed(){
    this.validateAll();
    let count = 0;
    for(let x=0; x<this.tableData.length; x++){
      let meta = this.metadata[this.tableData[x].contactId];
      if(!(meta.valid && meta.readSuccess)){
        count ++;
      }
    }
    if(count > 0){
      this.footerMessage = `Resolve issues before reprocessing. Remaining: ${count}`;
      return false;
    }
    this.footerMessage = '';
    return true;
  }

  validationStyle(item){
    return this.metadata[item.contactId].valid ? '' : 'error';
  }

  validateAll(){
    this.tableData.map(item => {
      let meta = this.metadata[item.contactId];
      let data = {};
      if(item.Attributes.hasOwnProperty('claimID') && meta['readSuccess']){
        data['valid'] = true;
        data['info'] = '';
        Object.assign(this.metadata[item.contactId], data);
      }else{
        data['valid'] = false;
        if(meta.readSuccess){
          data['info'] = 'missing claim number';
        }
        Object.assign(this.metadata[item.contactId], data);
      }
    })
  }

  validateItem(item: any, showMessage: boolean = false) {
    this.formValid = false;
    if (showMessage) {
      this.lambdaError = false;
      this.lambdaMessage = '';
    }
    if (!item.Attributes.hasOwnProperty('claimID')) {
      if (showMessage) { console.log("showing message!"); this.lambdaError = true; this.lambdaMessage = 'claimID field is required' }
      return false;
    }
    if (item.Attributes.claimID.length == 0) {
      if (showMessage) { console.log("showing message!"); this.lambdaError = true; this.lambdaMessage = 'claimID field is required' }
      return false;
    }
    this.formValid = true;
    return true;
  }

  handleEvent(event) {
    console.log(event);
    if (event.action === 'put') {
      this.replaceRowInTable(event.item);
      this.validateAll();
      this.applyFilter();
    }
  }

  handleButtonEvent(event) {
    console.log(event);
    this.disableButtons = (event == 'edit');
  }

  cancel() {
    this.tableData = [];
    this.filteredData = []
    this.showTable = false;
  }

  delay(ms) {
    return new Promise(res => setTimeout(res, ms));
  }

  //load a csv of contactIds to be read from connect
  uploadFile($event) {
    this.lambdaError = false;
    this.lambdaMessage = "";
    let selectedFile = null;
    this.tableData = [];
    this.showTable = false;
    let csv: string = ""
    selectedFile = $event.target.files[0];
    console.log('name: ', selectedFile.name);
    console.log('size: ', selectedFile.size);
    console.log('type: ', selectedFile.type);
    let reader: FileReader = new FileReader();
    reader.readAsText(selectedFile);
    reader.onload = (e) => {
      console.log('e: ', e);
      csv = reader.result as string;
      $event.target.value = "";
      let rows = csv.split('\n');
      // handle empty rows
      for (let x = 0; x < rows.length; x++) {
        if (rows[x].split(',').join('').trim() == '') {
          rows.splice(x, 1);
          x--;
        }
      }
      if (rows[rows.length - 1] === "") {
        rows.pop();
      }
      console.log('rows: ', rows);
      let temp = [];
      rows.map(x => {
        let data = x.split(",");
        if (data.length < 5) {
          for (let c = 5 - data.length; c > 0; c--) {
            data.push("");
          }
        }
        let obj = { "contactId": data[0].trim(), "Attributes": {} };
        temp.push(obj);
      });
      this.showTable = true;
      this.updateMetadata(temp);
      this.readContactRecords(temp);
    }
  }

}
